import { useEffect, useRef, useState } from "react";
import { ChevronDown, SlidersHorizontal } from "../../compose/Icon";
import {
  type ReviewSort,
  REVIEW_SORT_OPTIONS,
} from "../../lib/reviewSort";

/**
 * The REVIEW NOTES header sort control (review-notes enhancements §1.5): a small
 * button-menu (reusing the ⋯-menu outside-click/Esc dismiss pattern already in
 * `CommentCardBar`) rather than a native `<select>`, for theme consistency + a11y
 * control. Right-aligned in the header. Applies to BOTH `ReviewRail` and
 * `CommentGutter` via this shared component (one sort surface, not two).
 */
export function ReviewNotesSort({
  sort,
  onSort,
}: {
  sort: ReviewSort;
  onSort: (next: ReviewSort) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = REVIEW_SORT_OPTIONS.find((o) => o.value === sort);

  const pick = (next: ReviewSort) => {
    setOpen(false);
    onSort(next);
  };

  return (
    <div className="review-sort" ref={wrapRef}>
      <button
        type="button"
        className="review-sort__trigger"
        data-testid="review-notes-sort"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Sort review notes (${active?.label ?? "Document order"})`}
        title="Sort review notes"
        onClick={() => setOpen((o) => !o)}
      >
        <SlidersHorizontal size={13} strokeWidth={1.75} />
        <span className="review-sort__label">{active?.label ?? "Sort"}</span>
        <ChevronDown size={12} strokeWidth={1.75} aria-hidden="true" />
      </button>
      {open ? (
        <div
          className="review-sort__menu"
          role="listbox"
          aria-label="Sort review notes"
          data-testid="review-notes-sort-menu"
        >
          {REVIEW_SORT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === sort}
              aria-checked={o.value === sort}
              className={`review-sort__option${o.value === sort ? " review-sort__option--active" : ""}`}
              data-testid={`sort-option-${o.value}`}
              onClick={() => pick(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
